import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useRecentItems, { RecentItem } from '../../hooks/useRecentItems';
import './RecentItems.css';

/** SVG icons for each item type */
function TypeIcon({ type }: { type: RecentItem['type'] }) {
  switch (type) {
    case 'request':
      return (
        <svg className="recent-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      );
    case 'mission':
      return (
        <svg className="recent-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
          <line x1="4" y1="22" x2="4" y2="15" />
        </svg>
      );
    case 'schedule':
      return (
        <svg className="recent-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      );
    case 'patient':
      return (
        <svg className="recent-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    default:
      return null;
  }
}

/** Format a timestamp into a human-readable relative string */
function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
}

/** Capitalize the first letter of a type label */
function typeLabel(type: RecentItem['type']): string {
  switch (type) {
    case 'request': return 'Requests';
    case 'mission': return 'Missions';
    case 'schedule': return 'Schedules';
    case 'patient': return 'Patients';
    default: return type;
  }
}

/** Group items by type, preserving sort order within each group */
function groupByType(items: RecentItem[]): Record<string, RecentItem[]> {
  const groups: Record<string, RecentItem[]> = {};
  for (const item of items) {
    if (!groups[item.type]) {
      groups[item.type] = [];
    }
    groups[item.type].push(item);
  }
  return groups;
}

function RecentItems() {
  const { getRecentItems, clearRecentItems } = useRecentItems();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const items = getRecentItems();
  const grouped = groupByType(items);
  // Order groups by most recent item in each group
  const groupOrder = Object.keys(grouped).sort((a, b) => {
    const aLatest = grouped[a][0]?.timestamp ?? 0;
    const bLatest = grouped[b][0]?.timestamp ?? 0;
    return bLatest - aLatest;
  });

  // Close popover when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleItemClick = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const handleClear = () => {
    clearRecentItems();
    setOpen(false);
  };

  return (
    <div className="recent-items-container" ref={containerRef}>
      <button
        className="recent-items-trigger"
        onClick={() => setOpen(prev => !prev)}
        title="Recent items"
        aria-label="Recent items"
        aria-expanded={open}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      </button>

      {open && (
        <div className="recent-items-popover" role="menu">
          <div className="recent-items-header">
            <span className="recent-items-title">Recent Items</span>
            {items.length > 0 && (
              <button
                className="recent-items-clear"
                onClick={handleClear}
              >
                Clear all
              </button>
            )}
          </div>

          <div className="recent-items-body">
            {items.length === 0 ? (
              <div className="recent-items-empty">No recent items</div>
            ) : (
              groupOrder.map(type => (
                <div key={type} className="recent-items-group">
                  <div className="recent-items-group-label">
                    {typeLabel(type as RecentItem['type'])}
                  </div>
                  {grouped[type].map(item => (
                    <button
                      key={`${item.type}-${item.id}`}
                      className="recent-item"
                      onClick={() => handleItemClick(item.path)}
                      role="menuitem"
                    >
                      <TypeIcon type={item.type} />
                      <span className="recent-item-label">{item.label}</span>
                      <span className="recent-item-time">
                        {formatRelativeTime(item.timestamp)}
                      </span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default RecentItems;
